/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material';
import { formatDateLocal, type GlobalFields, SalesforceObject } from '@poweralpha/common';
import { useMemo, useState } from 'react';
import { useProjectPricingGlobalDataQuery } from '../../bid-requests/api';
import { useSfIdMapQuery } from '../../project/api';
import PlantTypeIcon from '../../project/landing-page/PlantTypeIcon';
import RecordList from '../../react-components/RecordList';
import { InfoPill } from '../../react-components/text';
import Widget from '../../react-components/Widget';
import { formatDollar } from '../../utils';

const PAGE_SIZE = 10;

const formatTransactionSummary = (pricing: GlobalFields) => {
  if (pricing.transaction === 'BTA') {
    return `BTA: ${formatDollar(pricing.bta_price)}`;
  }

  const details: string[] = [];
  if ((pricing.ppa_price ?? 0) > 0) {
    details.push(`PPA: ${formatDollar(pricing.ppa_price)}/MWh`);
  }
  if ((pricing.capacity_payment ?? 0) > 0) {
    details.push(`Capacity: ${formatDollar(pricing.capacity_payment, 0, 1, true)} /MW/month`);
  }
  return details.join(' | ') || 'PPA: -';
};

const formatScenarioDetails = (pricing: GlobalFields) => {
  const details: string[] = [];
  if (pricing.pv_capacity) {
    details.push(`PV: ${pricing.pv_capacity.toFixed(1)} MW-ac`);
  }
  if (pricing.ess_capacity) {
    details.push(`ESS: ${pricing.ess_capacity.toFixed(1)} MWh-ac`);
  }
  if (pricing.cod) {
    details.push(`COD: ${formatDateLocal(pricing.cod, true)}`);
  }
  return details.join(' | ') || '-';
};

function RecentPricings() {
  const [limit, setLimit] = useState(PAGE_SIZE);

  const recentPricingsQuery = useProjectPricingGlobalDataQuery({
    apiOptions: { limit, status: 'Completed', sort_by: 'modified_at', sort_direction: 'desc' },
    then: (data) => data.data,
  });

  const opportunityIds = useMemo(
    () =>
      Array.from(
        new Set(
          (recentPricingsQuery.data ?? [])
            .map((row) => row.opportunity_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [recentPricingsQuery.data],
  );

  const { data: opportunityIdMap } = useSfIdMapQuery(SalesforceObject.opportunity, opportunityIds);

  return (
    <Widget
      title="Recent pricings"
      actions={
        <IconButton
          loading={recentPricingsQuery.isRefetching}
          size="small"
          onClick={() => recentPricingsQuery.refetch()}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
      redirectPath="/bid-requests/pricing-dashboard"
      titleLink
    >
      <RecordList
        label="pricing"
        queryResult={recentPricingsQuery}
        idKey="id"
        listItemHeight={66}
        getLink={(pricing) => `/bid-requests/${pricing.bid_request_id}/scenarios`}
        onLoadMore={() => setLimit((current) => current + PAGE_SIZE)}
        isLoadingMore={recentPricingsQuery.isFetching && !recentPricingsQuery.isLoading}
        getRow={(pricing) => {
          const opportunityName = pricing.opportunity_id
            ? (opportunityIdMap?.[pricing.opportunity_id] ?? pricing.opportunity_id)
            : (pricing.scenario ?? pricing.name);
          return (
            <Stack
              spacing={0.45}
              sx={{
                width: '100%',
                position: 'relative',
                pr: 9,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.25,
                }}
              >
                <InfoPill label={pricing.transaction} height={18} />
                <Tooltip title={`Power Plant Type: ${pricing.plant_type ?? '-'}`}>
                  <Box
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 0,
                    }}
                  >
                    <PlantTypeIcon type={pricing.plant_type} />
                  </Box>
                </Tooltip>
              </Box>
              <MuiLink
                component="span"
                underline="always"
                sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
                noWrap
              >
                {opportunityName}
              </MuiLink>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}
              >
                {formatScenarioDetails(pricing)}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.1,
                }}
              >
                {formatTransactionSummary(pricing)}
              </Typography>
            </Stack>
          );
        }}
        hasMore
      />
    </Widget>
  );
}

export default RecentPricings;
